// Copy Campaign dialog + trigger button used on the campaign detail page.
// Clones a campaign into another org's namespace (script, tone, voice,
// call-window, DNC attachments, retries, minutes-cap, alerts) but drops the
// recipient list and all attempt history — the copy lands as an inactive
// Draft in the target org.
//
// Access model (mirrors backend):
//   • staff   → can copy into ANY partner or customer org
//   • partner → can copy into own org + any customer child org
//   • customer→ this button never renders (backend also 403s)
//
// Note: backend endpoint + field names still use "fork" (data compatibility);
// only the user-facing labels use "Copy".

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export function ForkCampaignButton({ campaign }) {
  const { user } = useAuth();
  const role = ((user?.base_role || user?.role) || "").toLowerCase();
  const canCopy = role === "staff" || role === "partner";
  const [open, setOpen] = useState(false);
  if (!canCopy) return null;   // customers don't get the button
  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="ai-outbound-fork-campaign-btn"
        className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300"
      >
        <Copy className="h-4 w-4" />
        Copy to org
      </Button>
      {open && <CopyCampaignDialog campaign={campaign} role={role}
                                   open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

function CopyCampaignDialog({ campaign, role, open, onClose }) {
  const navigate = useNavigate();
  const isStaff = role === "staff";
  const [orgs, setOrgs] = useState(null);
  const [targetOrgId, setTargetOrgId] = useState("");
  const [newName, setNewName] = useState(`${campaign?.name || "Campaign"} (copy)`);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTargetOrgId("");
    setNewName(`${campaign?.name || "Campaign"} (copy)`);
    setOrgs(null);
    (async () => {
      try {
        // Staff sees all partners + customers. Partners see their own org + any
        // customer whose parent_org_id === their own org (backend still enforces).
        const [{ data: p }, { data: c }] = await Promise.all([
          api.get("/organizations?type=partner"),
          api.get("/organizations?type=customer"),
        ]);
        const rows = [...(p.organizations || p || []), ...(c.organizations || c || [])]
          .map((o) => ({ org_id: o.org_id, name: o.name, type: o.type,
                         parent_org_id: o.parent_org_id }));
        const filtered = isStaff ? rows : rows.filter((o) => {
          const own = campaign?.org_id;   // detail page is scoped so this is always my org
          return o.org_id === own || o.parent_org_id === own;
        });
        filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOrgs(filtered);
      } catch { setOrgs([]); }
    })();
  }, [open, isStaff, campaign?.name, campaign?.org_id]);

  const copy = async () => {
    if (!targetOrgId) { toast.error("Pick a target organization"); return; }
    setSaving(true);
    try {
      // Backend endpoint keeps the historical /fork path.
      const { data } = await api.post(`/outbound/campaigns/${campaign.campaign_id}/fork`, {
        target_org_id: targetOrgId,
        name: newName.trim() || undefined,
      });
      const targetName = orgs?.find((o) => o.org_id === targetOrgId)?.name || targetOrgId;
      toast.success(`Copied "${campaign.name}" into ${targetName}`);
      onClose();
      navigate(`/ai-outbound/${data.campaign_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Copy failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="ai-outbound-fork-campaign-dialog">
        <DialogHeader>
          <DialogTitle>Copy campaign to another organization</DialogTitle>
          <DialogDescription>
            Clones the script, voice, call window, DNC lists, retry rules and alert
            config into the target org as an <strong>inactive Draft</strong>. The recipient
            list is <strong>not</strong> copied — someone in the target org attaches
            their own list before running.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">New campaign name</label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                   data-testid="ai-outbound-fork-campaign-name" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Target organization</label>
            {orgs === null ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading organizations…
              </div>
            ) : orgs.length === 0 ? (
              <div className="text-xs text-red-600">
                {isStaff
                  ? "No organizations found."
                  : "No child organizations available to copy into."}
              </div>
            ) : (
              <Select value={targetOrgId} onValueChange={setTargetOrgId}>
                <SelectTrigger data-testid="ai-outbound-fork-campaign-org-select">
                  <SelectValue placeholder="Pick an org…" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => (
                    <SelectItem key={o.org_id} value={o.org_id}>
                      <span className="mr-1 rounded-full bg-slate-100 px-1.5 text-[10px] font-medium uppercase text-slate-500">
                        {o.type}
                      </span>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="ai-outbound-fork-campaign-cancel">Cancel</Button>
          <Button onClick={copy} disabled={saving || !targetOrgId}
                  data-testid="ai-outbound-fork-campaign-confirm"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
            Copy campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
